import { DATE_FORMATS, formatDateByFormat, formatTenureYears, getFlexibleDurationMonths } from '../../../../../../../utils/dateUtils';
import type { RoleFields, Education, CountRowStatus } from './ManagementUserProfile.types';

export const AION_INGEST_LITIGATION_CLASSIFICATION_DATA = 'AION_INGEST_LITIGATION_CLASSIFICATION_DATA';

/** Reads the job title from whichever of the plausible keys the payload uses — the backend hasn't settled on one. */
export const getEntryRole = (entry?: RoleFields): string | undefined => {
    const candidates = [entry?.role, entry?.title, entry?.designation];
    return candidates.find((value): value is string => typeof value === 'string' && value.trim().length > 0);
};

/** Joins degree and fieldOfStudy, skipping the field when the degree already names it. */
export const getEducationSubline = (entry?: Education): string | undefined => {
    const degree = entry?.degree?.trim();
    const field = entry?.fieldOfStudy?.trim();
    if (!degree) return field || getEntryRole(entry);
    if (!field || degree.toLowerCase().includes(field.toLowerCase())) return degree;
    return `${degree}, ${field}`;
};

/** Prefers the enriched list, but only when it has entries — an empty one must not mask a populated fallback. */
export function pickEnriched<T>(enriched?: T[], fallback?: T[]): T[] | undefined {
    return enriched && enriched.length ? enriched : fallback;
}

/** Groups the enriched raw count (3681 to "3,681"); an already-formatted string passes through. */
export const formatFollowersCount = (count?: number | string | null): string | undefined => {
    if (count === null || count === undefined || count === '') return undefined;
    return typeof count === 'number' ? count.toLocaleString('en-IN') : String(count);
};

/** A missing end date means "ongoing" only for the most recent (first) entry. */
export const getEntryDurationMonths = (entry?: { startDate?: string; endDate?: string }, index?: number): number | null => {
    if (!entry?.endDate && index !== 0) return null;
    return getFlexibleDurationMonths(entry?.startDate, entry?.endDate);
};

/** 0 and null are different facts: 0 is "screened, nothing found", null is "never screened". Do not collapse them. */
export const getCountRowStatus = (
    count: number | null | undefined,
    params: { locked?: boolean; notReady?: boolean; unitSingular: string; unitPlural: string; zeroLabel: string },
): CountRowStatus => {
    const { notReady, unitSingular, unitPlural, zeroLabel } = params;
    if (notReady) return { kind: 'dash', reason: 'notReady' };
    if (count === null || count === undefined) return { kind: 'dash', reason: 'absent' };
    if (count > 0) return { kind: 'alert', label: `${count} ${count === 1 ? unitSingular : unitPlural}` };
    return { kind: 'positive', label: zeroLabel };
};

/** Blank counts as absent: `Number('')` is 0, not NaN, so a blank score would otherwise read as valid. */
export const hasValidCreditScore = (score?: number | null): score is number =>
    score !== null && score !== undefined && String(score).trim() !== '' && !Number.isNaN(Number(score));

/** Duration and range are computed independently, so an unparseable half never blanks out the other. */
export const computeTenureDisplay = (
    tenureBeginDate?: number,
    tenureEndDate?: number,
    isActive?: boolean,
): { durationLabel: string | null; rangeLabel: string | null } => {
    const durationRaw = tenureBeginDate ? formatTenureYears(tenureBeginDate, tenureEndDate) : null;
    const durationLabel = durationRaw && durationRaw !== '-' ? durationRaw : null;

    const beginFormatted = tenureBeginDate ? formatDateByFormat(tenureBeginDate, DATE_FORMATS.MONTH_SHORT_YEAR) : '';
    const endFormatted = tenureEndDate ? formatDateByFormat(tenureEndDate, DATE_FORMATS.MONTH_SHORT_YEAR) : isActive ? 'Present' : '';
    const rangeLabel = tenureBeginDate || tenureEndDate ? `(${beginFormatted || 'NA'} - ${endFormatted || 'NA'})` : null;

    return { durationLabel, rangeLabel };
};

/** Says the DATA is unavailable, not that the director has none. Also the only wording that fits the stat card. */
export const NOT_AVAILABLE_LABEL = 'Not available';

/** Omits the slot when neither end of the range is known, rather than printing "NA - NA". */
export const buildEntryDateRange = (fromDate?: string | null, toDate?: string | null, isOngoing = false): string | undefined => {
    const to = toDate || (isOngoing ? 'Present' : undefined);
    if (!fromDate && !to) return undefined;
    return `${fromDate || 'NA'} - ${to || 'NA'}`;
};
